import { useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Body1,
  Button,
  Select,
} from "@fluentui/react-components";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "@fluentui/react-components/unstable";
import PageTitle from "../../../components/common/PageTitle";
import AdminNavbar from "../../../components/layouts/AdminNavbar";
import { CenteredLayout } from "../../../components/layouts";
import AddResourceAllocationOrderDialog from "../../../components/layouts/AddResourceAllocationOrderDialog";
import useResourceAllocationOrders from "../../../hooks/useResourceAllocationOrders";
import { AppLoader } from "../../../components/common";
import dataTimeService from "../../../services/dataTime.service";
import dateFormatService from "../../../services/dateFormat.service";
import { monthNames } from "../../../utils/consts";
import styles from "../../../components/layouts/EmplyeesListStyles";

type RouteParams = {
  employeeId: string;
};

function parseQuery(search: string) {
  const params = new URLSearchParams(search);
  const year = parseInt(params.get("year") ?? "", 10);
  const month = parseInt(params.get("month") ?? "", 10);
  return {
    year: Number.isFinite(year) ? year : new Date().getFullYear(),
    month: Number.isFinite(month) && month >= 1 && month <= 12 ? month : new Date().getMonth() + 1,
  };
}

type LocationState = {
  employeeName?: string;
};

const ResourceAllocationEmployeeDetail = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { employeeId } = useParams<RouteParams>();
  const { year, month } = useMemo(() => parseQuery(location.search), [location.search]);
  const selectedMonthLabel = monthNames[month - 1] ?? monthNames[0];

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [openAddDialog, setOpenAddDialog] = useState(false);

  const {
    orders,
    projects,
    isLoading,
    isSubmitting,
    createOrder,
  } = useResourceAllocationOrders(employeeId, year, month, selectedProjectId || undefined);

  const selectedProject = projects.find((p) => p.youTrackProjectId === selectedProjectId) ?? null;
  const employeeName =
    location.state?.employeeName ??
    (orders[0] ? `${orders[0].lastName} ${orders[0].firstName}` : "");

  const goBack = () => {
    history.push(
      `/resource-allocation?year=${year}&month=${month}`
    );
  };

  return (
    <>
      <AdminNavbar />
      <CenteredLayout>
        <Body1>
          <Button appearance="primary" onClick={goBack} style={styles.buttonBack}>
            Powrót
          </Button>
        </Body1>
        <PageTitle
          title={
            employeeName
              ? `Zlecenia — ${employeeName}`
              : "Zlecenia godzin pracy"
          }
        />
        <Card style={styles.tableContainer}>
          <Body1>
            <div style={styles.expectedHoursToolbarRow}>
              Miesiąc:
              <Select style={styles.tableFilter} value={selectedMonthLabel} disabled>
                <option value={selectedMonthLabel}>{selectedMonthLabel}</option>
              </Select>
              Rok:
              <Select style={styles.tableFilterYear} value={year} disabled>
                <option value={year}>{year}</option>
              </Select>
              Nazwa projektu:
              <Select
                style={styles.tableFilter}
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
              >
                <option value="">Wszystkie</option>
                {projects.map((project) => (
                  <option key={project.youTrackProjectId} value={project.youTrackProjectId}>
                    {project.projectName}
                  </option>
                ))}
              </Select>
              <Button
                appearance="primary"
                disabled={!selectedProjectId}
                onClick={() => setOpenAddDialog(true)}
              >
                Dodaj zlecenie
              </Button>
            </div>

            <div className="main-table-container">
              <Table noNativeElements aria-label="Tabela zleceń alokacji zasobów" style={styles.table}>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>Nazwisko i imię</TableHeaderCell>
                    <TableHeaderCell>Nazwa projektu</TableHeaderCell>
                    <TableHeaderCell>Nazwa zlecenia</TableHeaderCell>
                    <TableHeaderCell>Zlecone godziny</TableHeaderCell>
                    <TableHeaderCell>Data i czas zlecenia</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.length === 0 && !isLoading && (
                    <h4 className="table-empty-value">Brak danych</h4>
                  )}
                  {orders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        <TableCellLayout>{`${order.lastName} ${order.firstName}`}</TableCellLayout>
                      </TableCell>
                      <TableCell>
                        <TableCellLayout>{order.youTrackProjectLabel}</TableCellLayout>
                      </TableCell>
                      <TableCell>
                        <TableCellLayout>{order.assignmentDescription}</TableCellLayout>
                      </TableCell>
                      <TableCell>
                        <TableCellLayout>
                          {dataTimeService.convertFormat(order.durationMinutes)}
                        </TableCellLayout>
                      </TableCell>
                      <TableCell>
                        <TableCellLayout>
                          {`${dateFormatService.formatDate(order.createTime)} ${dateFormatService.formatDateToTime(order.createTime)}`}
                        </TableCellLayout>
                      </TableCell>
                    </TableRow>
                  ))}
                  {isLoading && orders.length === 0 && <AppLoader />}
                </TableBody>
              </Table>
            </div>
          </Body1>
        </Card>

        <AddResourceAllocationOrderDialog
          open={openAddDialog}
          setOpen={setOpenAddDialog}
          selectedMonth={selectedMonthLabel}
          selectedYear={year}
          selectedProject={selectedProject}
          onSubmit={createOrder}
          isSubmitting={isSubmitting}
        />
      </CenteredLayout>
    </>
  );
};

export default ResourceAllocationEmployeeDetail;
